import React, { useState, useEffect } from "react";
import { getRequest, postRequest, patchRequest } from "../../axiosRoutes/index";
import {
    Button,
    Modal,
    Box,
    Typography,
    OutlinedInput,
    FormControl,
    InputLabel,
    Grid,
    MenuItem,
    Select,
    CircularProgress,
    Snackbar,
    Alert,
    Checkbox,
    FormControlLabel,
    IconButton,
    Divider,
} from "@mui/material";
import { useSnackbar } from "../../axiosRoutes/snackbarConfig";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';

const currentYear = dayjs().year();
const minDate = `${currentYear}-01-01`;
const maxDate = `${currentYear + 2}-12-31`;

const AddWorklog = ({ open, handleClose, worklogId }) => {
    const { snackbar, showSnackbar, handleSnackbarClose } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [worklogData, setWorklogData] = useState({
        userId: "",
        date: "",
        projects: [{ projectName: "", workHours: "", update: "" }],
        hasBlocker: false,
        blockerDetails: "",
        onLeave: false,
        work_location: 'on-site'
    });
    const [projectNames, setProjectNames] = useState()
    const [leaves, setLeaves] = useState([])

    const fetchProjectsNames = async (userId) => {
            const access = localStorage.getItem("access")
            try {
                setLoading(true);
                const response = await getRequest(`employee/project/user/${userId}`, {
                    headers: {
                        Authorization: `Bearer ${access}`,
                    },
                })
                const prjNames = response.map(value => value.title)
                setProjectNames(prjNames)
            } catch (error) {
                setProjectNames([])
                console.error("Error fetching Worklog:", error);
            } finally {
                setLoading(false);
            }
        }

    const fetchLeaves = async() => {
            try{
                const response = await getRequest(`filter/leave`)
                const filtered = Object.entries(response).filter(([key, value]) => {
                    return !value.includes("home") && !value.includes("half");
                });
                const finalObject = Object.fromEntries(filtered);
                setLeaves(finalObject)
            } catch (error) {
                console.error("Error fetching Leaves:", error);
            }
        }

    useEffect(() => {
        const fetchEmployees = async () => {
            const access = localStorage.getItem("access");
            try {
                const response = await getRequest("employee/getAllEmployees", {
                    headers: {
                        Authorization: `Bearer ${access}`,
                    },
                });
                const activeEmployees = response.filter((emp) => emp.status === "active");
                setEmployees(activeEmployees);
            } catch (error) {
                console.error("Error fetching Employees:", error);
            }
        };
        fetchEmployees();
        fetchLeaves()
    }, []);

    useEffect(() => {
        const fetchWorklog = async () => {
            if (worklogId !== null) {
                const access = localStorage.getItem("access");
                try {
                    setLoading(true);
                    const response = await getRequest(`admin/worklog/${worklogId}`, {
                        headers: {
                            Authorization: `Bearer ${access}`,
                        },
                    });
                    const formatDate = new Date(response.date).toISOString().split("T")[0];
                    setWorklogData({
                        userId: response.userId,
                        date: formatDate,
                        projects: (response?.projects ?? []).map(project => {
                            if (!projectNames?.includes(project.projectName)) {
                                return {
                                    ...project,
                                    customName: project.projectName,
                                    projectName: "others",
                                };
                            }
                            return project;
                        }),
                        hasBlocker: response.hasBlocker,
                        blockerDetails: response.blockerDetails || "",
                        work_location: response?.work_location || 'on-site',
                        onLeave: response.onLeave || false,
                        leaveType: response.leaveType || ''
                    });
                } catch (error) {
                    console.error("Error fetching Worklog:", error);
                } finally {
                    setLoading(false);
                }
            } else {
                setWorklogData({
                    userId: "",
                    date: dayjs().format('YYYY-MM-DD'),
                    projects: [{ projectName: "", workHours: "", update: "" }],
                    hasBlocker: false,
                    blockerDetails: "",
                    work_location: 'on-site',
                    onLeave: false,
                });
            }
        };
        fetchWorklog();
    }, [worklogId]);

    useEffect(() => {
        if (worklogData.userId){
            fetchProjectsNames(worklogData.userId)
        }
    }, [worklogData])

    const handleUpdate = async (e) => {
        e.preventDefault();
        worklogData.projects.forEach(project => {
            if (project.projectName === 'others') {
                project.projectName = project.customName
                delete project.customName;
            }
        });
        const prjNames = worklogData.projects.map(value => value.projectName.toLowerCase())
        const hasDuplicate = prjNames.some((name, index) => prjNames.includes(name, index + 1));
        if(hasDuplicate){
            showSnackbar('Project names must be unique. Duplicate names found!', "error");
            return
        }
        const totalHours = Number(worklogData.projects.reduce(
            (sum, project) => sum + Number(project.workHours),
            0
        ))
        
        if(!worklogData.onLeave && (totalHours <= 3 || totalHours >= 14)) {
            showSnackbar('Work hours should be between 3 and 14.', "error")
            return;
        }
        setLoading(true);
        try {
            const access = localStorage.getItem("access");
            
            // Prepare data based on onLeave status
            let dataToSend;
            if (worklogData.onLeave) {
                dataToSend = {
                    ...worklogData,
                    date: worklogData.date || new Date().toISOString().split('T')[0], 
                    projects: worklogData.leaveType !== 'unpaid' ? [{projectName: '', workHours: 8, update: ''}] : [],
                    hasBlocker: false,
                    blockerDetails: ""
                };
            } else {
                dataToSend = worklogData;
            }
            console.log("Data to send:", dataToSend);
            await patchRequest(
                `admin/worklog/${worklogId}`,
                {
                    Authorization: `Bearer ${access}`,
                },
                dataToSend
            );
            showSnackbar("Worklog Updated Successfully", "success");
            handleClose();
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || error.message || "An error occurred";
            showSnackbar(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        if (name === "hasBlocker") {
            setWorklogData({ ...worklogData, hasBlocker: checked, blockerDetails: checked ? worklogData.blockerDetails : "" });
        } else if (name === "onLeave") {
            if (checked) {
                // Clear other fields when onLeave is checked
                setWorklogData({ 
                    ...worklogData, 
                    onLeave: true,
                    date: worklogData.date,
                    projects: [{ projectName: "", workHours: "", update: "" }],
                    hasBlocker: false,
                    blockerDetails: ""
                });
            } else {
                setWorklogData({ ...worklogData, onLeave: false });
            }
        } else {
            setWorklogData({ ...worklogData, [name]: value });
        }
    };

    const handleProjectChange = (index, field, value) => {
        const updatedProjects = [...worklogData.projects];
        updatedProjects[index][field] = value;
        setWorklogData({ ...worklogData, projects: updatedProjects });
    };

    const addProject = () => {
        setWorklogData({
            ...worklogData,
            projects: [...worklogData.projects, { projectName: "", workHours: "", update: "" }],
        });
    };

    const removeProject = (index) => {
        if (worklogData.projects.length > 1) {
            const updatedProjects = worklogData.projects.filter((_, i) => i !== index);
            setWorklogData({ ...worklogData, projects: updatedProjects });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        worklogData.projects.forEach(project => {
            if (project.projectName === 'others') {
                project.projectName = project.customName
                delete project.customName;
            }
        });
        const totalHours = Number(worklogData.projects.reduce(
            (sum, project) => sum + Number(project.workHours),
            0
        ))
        
        if(!worklogData.onLeave && (totalHours <= 3 || totalHours >= 14)) {
            showSnackbar('Work hours should be greater than 3 or less than 14', "error")
            return;
        }

        if (!worklogData.userId) {
            showSnackbar("Please select an employee", "error");
            return;
        }
        
        if (!worklogData.date) {
            showSnackbar("Please select a date", "error");
            return;
        }
        
        // Skip project and blocker validations if employee is on leave
        if (!worklogData.onLeave) {
            const hasEmptyProject = worklogData.projects.some(
                (project) => !project.projectName || !project.workHours || !project.update
            );
            if (hasEmptyProject) {
                showSnackbar("Please fill all project fields", "error");
                return;
            }

            if (worklogData.hasBlocker && !worklogData.blockerDetails) {
                showSnackbar("Please provide blocker details", "error");
                return;
            }
        }

        const prjNames = worklogData.projects.map(value => value.projectName.toLowerCase())
        const hasDuplicate = prjNames.some((name, index) => prjNames.includes(name, index + 1));
        if(hasDuplicate){
            showSnackbar('Project names must be unique. Duplicate names found!', "error");
            return
        }

        setLoading(true);
        try {
            const access = localStorage.getItem("access");
            
            // Prepare data based on onLeave status
            let dataToSend;
            if (worklogData.onLeave) {
                dataToSend = {
                    ...worklogData,
                    date: worklogData.date || new Date().toISOString().split('T')[0],
                    projects: worklogData.leaveType !== 'unpaid' ? [{projectName: '', workHours: 8, update: ''}] : [],
                    hasBlocker: false,
                    blockerDetails: ""
                };
            } else {
                dataToSend = worklogData;
            }
            await postRequest(
                "admin/worklog",
                {
                    Authorization: `Bearer ${access}`,
                },
                dataToSend
            );
            showSnackbar("Worklog Added Successfully", "success");
            setWorklogData({
                userId: "",
                date: "",
                projects: [{ projectName: "", workHours: "", update: "" }],
                hasBlocker: false,
                blockerDetails: "",
                onLeave: false,
            });
            handleClose();
        } catch (error) {
            const errorMessage =
                error.response?.data?.message || error.message || "An error occurred";
            showSnackbar(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal
                open={open}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90%", md: "50%" },
                        maxHeight: "90vh",
                        overflowY: "auto",
                        bgcolor: "black",
                        color: "#FEFEFE",
                        border: "1px solid #A9A9A9",
                        p: 2,
                        "& .MuiOutlinedInput-root": {
                            color: "#FEFEFE",
                            "& fieldset": {
                                borderColor: "#FEFEFE",
                            },
                            "&:hover fieldset": {
                                borderColor: "#FEFEFE",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: "#FEFEFE",
                            },
                        },
                        "& .MuiInputLabel-root": {
                            color: "#FEFEFE",
                        },
                        "& .MuiIconButton-root": {
                            color: "#FEFEFE",
                        },
                        "& .MuiCheckbox-root": {
                            color: "#FEFEFE",
                        },
                    }}
                >
                    <Typography
                        id="modal-modal-title"
                        variant="h4"
                        fontWeight="bold"
                        textAlign="center"
                        component="h1"
                        sx={{ mb: 2 }}
                    >
                        {worklogId ? "Update Worklog" : "Add Worklog"}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                                <InputLabel htmlFor="outlined-adornment-employee">
                                    Employee
                                </InputLabel>
                                <Select
                                    labelId="outlined-adornment-employee-label"
                                    id="outlined-adornment-employee"
                                    name="userId"
                                    label="Employee"
                                    readOnly={worklogId ? true : false}
                                    value={worklogData.userId}
                                    onChange={handleChange}
                                    sx={{ backgroundColor: "transparent" }}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 200,
                                                overflowY: "auto",
                                            },
                                        },
                                    }}
                                >
                                    {employees ? (
                                        employees.map((employee) => (
                                            <MenuItem
                                                key={employee.userId._id}
                                                value={employee.userId._id}
                                            >
                                                {employee.userId.name}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled={true}>No Data yet</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                                <InputLabel
                                    htmlFor="outlined-adornment-date"
                                    shrink
                                    sx={{
                                        backgroundColor: "#000",
                                        paddingX: "4px",
                                        marginLeft: "-4px",
                                    }}
                                >
                                    Date
                                </InputLabel>
                                <OutlinedInput
                                    id="outlined-adornment-date"
                                    type="date"
                                    name="date"
                                    label="Date"
                                    value={worklogData.date}
                                    onChange={handleChange}
                                    inputProps={{ min: minDate, max: maxDate, readOnly: worklogId }}
                                    sx={{ backgroundColor: "transparent" }}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={worklogData.onLeave}
                                        onChange={handleChange}
                                        name="onLeave"
                                    />
                                }
                                label="On Leave / Not Working"
                                sx={{ mb: 1 }}
                            />
                        </Grid>

                        {worklogData.onLeave && <Grid item xs={12}>
                            <FormControl>
                                <FormLabel id="demo-controlled-radio-buttons-group" sx={{
                                    color: 'white', "&.Mui-focused": {
                                        color: "white",
                                    },
                                }}>Leave Type</FormLabel>
                                <RadioGroup
                                    row
                                    aria-labelledby="demo-controlled-radio-buttons-group"
                                    name="leaveType"
                                    value={worklogData?.leaveType}
                                    onChange={handleChange}
                                    defaultValue={'on-site'}
                                >
                                    {Object.entries(leaves).map(([label, value]) => (
                                        <FormControlLabel
                                            key={value}
                                            value={value}
                                            control={
                                                <Radio
                                                    sx={{
                                                        color: "white",
                                                        '&.Mui-checked': { color: "white" }
                                                    }}
                                                />
                                            }
                                            label={label}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Grid>}

                        {!worklogData.onLeave && <Grid item xs={12}>
                                                    <FormControl>
                                                        <FormLabel id="demo-controlled-radio-buttons-group" sx={{
                                                            color: 'white', "&.Mui-focused": {
                                                                color: "white",
                                                            },
                                                        }}>Work Type</FormLabel>
                                                        <RadioGroup row
                                                            aria-labelledby="demo-controlled-radio-buttons-group"
                                                            name="work_location"
                                                            value={worklogData.work_location}
                                                            onChange={handleChange}
                                                            defaultValue={'on-site'}
                                                        >
                                                            <FormControlLabel value="on-site" control={<Radio sx={{
                                                                color: "white",                
                                                                '&.Mui-checked': { color: "white" }, 
                                                            }}
                                                            />} label="On site" />
                                                            <FormControlLabel value="remote" control={<Radio sx={{
                                                                color: "white",                
                                                                '&.Mui-checked': { color: "white" }, 
                                                            }}/>} label="remote" />
                                                            <FormControlLabel value="half_day" control={<Radio sx={{
                                                                color: "white",                
                                                                '&.Mui-checked': { color: "white" }, 
                                                            }}/>} label="Half Day" />
                                                        </RadioGroup>
                                                    </FormControl>
                                                </Grid>}
                        {!worklogData.onLeave && (
                            <>

                                <Grid item xs={12}>
                                    <Divider sx={{ borderColor: "#FEFEFE", my: 1 }} />
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 2,
                                        }}
                                    >
                                        <Typography variant="h6">Projects</Typography>
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={addProject}
                                            sx={{
                                                bgcolor: "#fff",
                                                color: "#000",
                                                "&:hover": { bgcolor: "#e1e1e1" },
                                            }}
                                        >
                                            Add Project
                                        </Button>
                                    </Box>
                                </Grid>

                                {worklogData.projects.map((project, index) => (
                            <Grid item xs={12} key={index}>
                                <Box
                                    sx={{
                                        border: "1px solid #FEFEFE",
                                        borderRadius: "8px",
                                        p: 2,
                                        position: "relative",
                                    }}
                                >
                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                        Project {index + 1}
                                    </Typography>
                                    {worklogData.projects.length > 1 && (
                                        <IconButton
                                            sx={{
                                                position: "absolute",
                                                top: 8,
                                                right: 8,
                                                color: "#F44336",
                                            }}
                                            onClick={() => removeProject(index)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel id="demo-simple-select-label">Project</InputLabel>
                                                <Select
                                                    labelId="demo-simple-select-label"
                                                    id="demo-simple-select"
                                                    value={project.projectName}
                                                    label="Project"
                                                    onChange={(e) =>
                                                        handleProjectChange(
                                                            index,
                                                            "projectName",
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    {projectNames && projectNames.map(value => <MenuItem value={value}>{value}</MenuItem>)}
                                                    <MenuItem value={'others'}>others</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        {project.projectName === 'others' && <Grid item xs={12} md={6}>
                                            <FormControl variant="outlined" fullWidth>
                                                <InputLabel>Project Name</InputLabel>
                                                <OutlinedInput
                                                    type="text"
                                                    label="Project Name"
                                                    value={project.customName}
                                                    onChange={(e) =>
                                                        handleProjectChange(
                                                            index,
                                                            "customName",
                                                            e.target.value
                                                        )
                                                    }
                                                    sx={{ backgroundColor: "transparent" }}
                                                    inputProps={{
                                                        maxLength: 1000,
                                                    }}
                                                />
                                            </FormControl>
                                        </Grid>}
                                        <Grid item xs={12} md={6}>
                                            <FormControl variant="outlined" fullWidth>
                                                <InputLabel>Work Hours</InputLabel>
                                                <OutlinedInput
                                                    type="Number"
                                                    label="Work Hours"
                                                    value={project.workHours}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (/^\d*\.?\d*$/.test(value)) {
                                                            handleProjectChange(index, "workHours", value);
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (['e', 'E', '+', '-'].includes(e.key)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    inputProps={{ inputMode: "decimal", min: 0, step: 0.5 }}
                                                    sx={{ backgroundColor: "transparent" }}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl variant="outlined" fullWidth>
                                                <InputLabel>Update</InputLabel>
                                                <OutlinedInput
                                                    type="text"
                                                    label="Update"
                                                    multiline
                                                    rows={3}
                                                    value={project.update}
                                                    onChange={(e) =>
                                                        handleProjectChange(
                                                            index,
                                                            "update",
                                                            e.target.value
                                                        )
                                                    }
                                                    sx={{ backgroundColor: "transparent" }}
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                                ))}

                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={worklogData.hasBlocker}
                                                onChange={handleChange}
                                                name="hasBlocker"
                                            />
                                        }
                                        label="Do you face any blocker?"
                                    />
                                </Grid>

                                {worklogData.hasBlocker && (
                                    <Grid item xs={12}>
                                        <FormControl variant="outlined" fullWidth sx={{ mb: 1 }}>
                                            <InputLabel htmlFor="outlined-adornment-blocker">
                                                Blocker Details
                                            </InputLabel>
                                            <OutlinedInput
                                                id="outlined-adornment-blocker"
                                                type="text"
                                                name="blockerDetails"
                                                label="Blocker Details"
                                                multiline
                                                rows={4}
                                                value={worklogData.blockerDetails}
                                                onChange={handleChange}
                                                sx={{ backgroundColor: "transparent" }}
                                                inputProps={{
                                                  maxLength: 1000, 
                                                }}
                                            />
                                        </FormControl>
                                    </Grid>
                                )}
                            </>
                        )}

                        {worklogId !== null ? (
                            <Grid item xs={6}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleUpdate}
                                    sx={{
                                        bgcolor: "#E5622D",
                                        "&:hover": { bgcolor: "#D0521B" },
                                    }}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <CircularProgress size={24} sx={{ color: "#FF5722" }} />
                                    ) : (
                                        "Update"
                                    )}
                                </Button>
                            </Grid>
                        ) : (
                            <Grid item xs={6}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleSubmit}
                                    sx={{
                                        bgcolor: "#E5622D",
                                        "&:hover": { bgcolor: "#D0521B" },
                                    }}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <CircularProgress size={24} sx={{ color: "#FF5722" }} />
                                    ) : (
                                        "Add"
                                    )}
                                </Button>
                            </Grid>
                        )}
                        <Grid item xs={6}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleClose}
                                disabled={loading}
                                sx={{ bgcolor: "#A9A9A9", "&:hover": { bgcolor: "#E1E1E1" } }}
                            >
                                Cancel
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Modal>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddWorklog;